import { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import NootLadderABI from './abis/NootLadder.abi.json';
import { useAbstractClient } from '@abstract-foundation/agw-react';
import { LADDER_CONTRACT_ADDRESS } from './utils/consts';
import { ethers } from 'ethers';
//import activeChain from '../session/lib/chain';

// Create the context
const NootLadderContractContext = createContext(null);

// Provider component
export const NootLadderContractProvider = ({ children }) => {
    const { data: agwClient } = useAbstractClient();
    const currentAddress = agwClient?.account.address || null;
    const [events, setEvents] = useState({
        gameStarted: [],
        roundWon: [],
        gameLost: [],
        gameWon: [],
        adminTransferred: [],
        randomProviderUpdated: [],
    });

    const wsProvider = useMemo(() =>
        new ethers.WebSocketProvider(
            process.env.NETWORK === "mainnet" 
                ? 'wss://api.mainnet.abs.xyz/ws' 
                : 'wss://api.testnet.abs.xyz/ws'
        ),
        [] // No dependencies since URL is constant
    );

    const wsContract = useMemo(() =>
        new ethers.Contract(LADDER_CONTRACT_ADDRESS, NootLadderABI, wsProvider),
        [] // Depends on wsProvider
    );

    useEffect(() => {
        console.log("useEffect called");
        if (!wsContract || !currentAddress) return;
        console.log("wsContract and currentAddress are defined");

        // event GameStarted(address indexed player, uint256 wager, uint8 turns, uint256 gameId);
        // event RoundWon(address indexed player, Card previousCard, Card newCard, Guess guess, uint8 turnsLeft);
        // event GameLost(address indexed player, Card previousCard, Card newCard, Guess guess);
        // event GameWon(address indexed player, uint256 prize);
        // event AdminTransferred(address indexed previousAdmin, address indexed newAdmin);

        // Add event listeners for player-specific events with filters
        const handleGameStarted = (result) => {
            const [player, wager, turns, gameId] = result.args;
            const eventData = { 
                player, 
                wager: wager.toString(), 
                turns: turns.toString(),
                gameId: gameId.toString()
            };
            console.log("GameStarted event for current user:", eventData);
            setEvents(prev => ({ 
                ...prev, 
                gameStarted: [...prev.gameStarted, eventData] 
            }));
        };

        const handleRoundWon = (result) => {
            const [player, previousCard, newCard, guess, turnsLeft] = result.args;
            const eventData = { 
                player, 
                previousCard: previousCard.toString(), 
                newCard: newCard.toString(), 
                guess: guess.toString(),
                turnsLeft: turnsLeft.toString() 
            };
            console.log("RoundWon event for current user:", eventData);
            setEvents(prev => ({ 
                ...prev, 
                roundWon: [...prev.roundWon, eventData] 
            }));
        };

        const handleGameLost = (result) => {
            const [player, previousCard, newCard, guess] = result.args;
            const eventData = { 
                player, 
                previousCard: previousCard.toString(), 
                newCard: newCard.toString(), 
                guess: guess.toString() 
            };
            console.log("GameLost event for current user:", eventData);
            setEvents(prev => ({ 
                ...prev, 
                gameLost: [...prev.gameLost, eventData] 
            }));
        };

        const handleGameWon = (result) => {
            const [player, prize] = result.args;
            const eventData = { 
                player, 
                prize: prize.toString() 
            };
            console.log("GameWon event for current user:", eventData);
            setEvents(prev => ({ 
                ...prev, 
                gameWon: [...prev.gameWon, eventData] 
            }));
        };
        
        // Set up event filters
        const gameStartedFilter = wsContract.filters.GameStarted(currentAddress);
        const roundWonFilter = wsContract.filters.RoundWon(currentAddress);
        const gameLostFilter = wsContract.filters.GameLost(currentAddress);
        const gameWonFilter = wsContract.filters.GameWon(currentAddress);

        // Listen with filters
        wsContract.on(gameStartedFilter, handleGameStarted);
        wsContract.on(roundWonFilter, handleRoundWon);
        wsContract.on(gameLostFilter, handleGameLost);
        wsContract.on(gameWonFilter, handleGameWon);

        // Remove event listeners when component unmounts
        return () => {
            wsContract.off(gameStartedFilter, handleGameStarted);
            wsContract.off(roundWonFilter, handleRoundWon);
            wsContract.off(gameLostFilter, handleGameLost);
            wsContract.off(gameWonFilter, handleGameWon);
        };
    }, [wsContract, currentAddress]);

    // Context value
    const clearEvents = useCallback(() => {
        setEvents({
            gameStarted: [],
            roundWon: [],
            gameLost: [],
            gameWon: [],
            adminTransferred: [],
            randomProviderUpdated: [],
        });
    }, []);

    const value = {
        wsProvider,
        wsContract,
        events,
        clearEvents,
        currentAddress
    };

    return (
        <NootLadderContractContext.Provider value={value}>
            {children}
        </NootLadderContractContext.Provider>
    );
};

// Hook to use the context
export const useNootLadderContract = () => {
    const context = useContext(NootLadderContractContext);
    if (context === null) {
        throw new Error('useNootLadderContract must be used within a NootLadderContractProvider');
    }
    return context;
};
